// /src/components/LangSwitcher.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SUPPORTED_LANGS, getLangFromPath, buildPathWithLang } from "../i18n/lang";

const svgToDataUri = (svg) =>
  `data:image/svg+xml,${encodeURIComponent(svg.replace(/\s+/g, " ").trim())}`;

const RAW_FLAGS = {
  uk: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 3"><path fill="#005bbb" d="M0 0h4v1.5H0z"/><path fill="#ffd500" d="M0 1.5h4V3H0z"/></svg>`,
  en: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30"><path fill="#012169" d="M0 0h60v30H0z"/><path fill="#FFF" d="M0 0l26 13.5H18L0 4.5V0zm34 13.5L60 0v4.5L42 13.5h-8zm-16 3L0 30v-4.5L18 16.5h8zm8 0l26 13.5V30L34 16.5h-8z"/><path fill="#C8102E" d="M0 0l26 13.5h-4L0 2V0zm34 13.5L60 0v2L38 13.5h-4zm-16 3L0 30v-2l22-10.5h4zm8 0l26 13.5v-2L38 16.5h-4z"/><path fill="#FFF" d="M24 0h12v30H24zM0 12h60v6H0z"/><path fill="#C8102E" d="M26 0h8v30h-8zM0 13h60v4H0z"/></svg>`,
  it: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2"><path fill="#009246" d="M0 0h1v2H0z"/><path fill="#fff" d="M1 0h1v2H1z"/><path fill="#ce2b37" d="M2 0h1v2H2z"/></svg>`,
  fr: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2"><path fill="#0055a4" d="M0 0h1v2H0z"/><path fill="#fff" d="M1 0h1v2H1z"/><path fill="#ef4135" d="M2 0h1v2H2z"/></svg>`,
};

const FLAG_DATA = Object.fromEntries(
  Object.entries(RAW_FLAGS).map(([lang, svg]) => [lang, svgToDataUri(svg)])
);

const DEFAULT_FLAG = svgToDataUri(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2 2"><path fill="#e2e8f0" d="M0 0h2v2H0z"/><circle cx="1" cy="1" r="0.65" fill="#94a3b8"/></svg>`
);

export const LANG_SWITCHER_BUTTON_STYLE = {
  background: "#fff",
  color: "#006e45",
  border: "2px solid #00723a",
  fontWeight: 700,
  fontSize: 17,
  borderRadius: 24,
  padding: "8px 14px 8px 18px",
  minWidth: 110,
  height: 42,
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  boxShadow: "0 2px 8px #006e4520",
  lineHeight: 1,
};

// Круглая иконка флага (маска-кружок + лёгкая тень)
const FlagCircle = ({ lang, size = 40, style = {} }) => (
  <span
    style={{
      width: size,
      height: size,
      borderRadius: 9999,
      overflow: "hidden",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
      background: "#fff",
      ...style,
    }}
  >
    <img
      src={FLAG_DATA[lang] || DEFAULT_FLAG}
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      loading="lazy"
    />
  </span>
);

// Подписи + эмодзи-флаги
const LANGUAGE_LABELS = {
  uk: { title: "Українська", subtitle: "Ukrainian", emoji: "🇺🇦" },
  en: { title: "English",    subtitle: "English",   emoji: "🇬🇧" },
  it: { title: "Italiano",   subtitle: "Italian",   emoji: "🇮🇹" },
  fr: { title: "Français",   subtitle: "French",    emoji: "🇫🇷" },
};


export default function LangSwitcher({ className = "" }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const current = getLangFromPath(loc.pathname);
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  const change = (lng) => {
    if (!SUPPORTED_LANGS.includes(lng)) return;
    if (lng === current) {
      setOpen(false);
      return;
    }
    setOpen(false);
    try { localStorage.setItem("lang", lng); } catch { /* ignore */ }
    const to = buildPathWithLang(lng, loc.pathname, loc.search, loc.hash);
    navigate(to, { replace: false });
  };

  React.useEffect(() => {
    const onOutside = (e) => {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside, { passive: true });
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  React.useEffect(() => { setOpen(false); }, [current]);

  const label = current.toUpperCase();

  return (
    <div
      ref={rootRef}
      className={className}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        style={{
          ...LANG_SWITCHER_BUTTON_STYLE,
          textTransform: "uppercase",
        }}
      >
        {label}
        <span aria-hidden style={{ fontSize: 16, opacity: 0.7, marginLeft: 2 }}>▾</span>
        {/* Флажок справа от стрелочки */}
        <FlagCircle lang={current} size={22} style={{ marginLeft: 2 }} />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            bottom: "calc(100% + 20px)",     // чуть больше отступа
            left: 0,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 18,
            boxShadow: "0 18px 44px rgba(0,0,0,0.20)",
            zIndex: 1000,
            padding: 16,
            minWidth: 560,                    // модалка больше
            maxWidth: 680,
            overflow: "hidden",
          }}
        >
          {/* Увеличенная зелёная шапка */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 16,                     // было 8 → стало 16
              background: "#00723a",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
            }}
          />
          <div style={{ marginTop: 10 }} />

          {/* Сетка 3 колонки с одинаковыми кружками */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",   // 3 в ряд как на скрине
              gap: 12,
            }}
          >
            {SUPPORTED_LANGS.map((lng) => {
              const meta = LANGUAGE_LABELS[lng] || { title: lng.toUpperCase(), subtitle: "", emoji: "🌐" };
              const isCurrent = lng === current;
              return (
                <button
                  type="button"
                  key={lng}
                  role="option"
                  aria-selected={isCurrent}
                  onClick={() => change(lng)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: isCurrent ? "2px solid #00723a" : "1px solid #e5e7eb",
                    background: isCurrent ? "#e8f5ee" : "#fff",
                    color: "#0f172a",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <FlagCircle lang={lng} size={44} />   {/* одинаковые круглые иконки */}
                  <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <span style={{ fontWeight: 800 }}>{meta.title}</span>
                    {meta.subtitle ? (
                      <span style={{ fontSize: 12, color: "#64748b" }}>{meta.subtitle}</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
